'use client'

import { useEffect, useRef, useState } from 'react'
import { Download, Loader2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { UserText } from '@/components/ui/user-text'
import { useTranslation } from '@/lib/i18n'
import { fetchKnowledgeFile, type KnowledgeFileRealm } from '@/lib/knowledge/portal-api'

/**
 * The document behind a source (K1 contract: "a card opens the document viewer
 * (PDF inline at the page via an authorised blob URL, `#page=N`; other types
 * download)"). The file is fetched with the bearer, since a plain link carries
 * no token, and shown from a blob URL that is revoked when the viewer closes.
 *
 * A PDF is an <object type="application/pdf"> whose fallback content is a way
 * out: "If the document doesn't show here, download it." with Download
 * (knowledge polish, 2026-10-04). The demo's viewer was blank: an <iframe>
 * shows nothing where the browser has no PDF viewer. Measured: headless
 * Chromium 145 reports `navigator.pdfViewerEnabled` false, leaves the frame
 * empty and starts a download; an <object> there shows its fallback; headed
 * Chrome 154 renders the blob at the page asked for, in either. A browser that
 * says it has no viewer (Chrome on Android too) gets the fallback without an
 * embed. The blob is re-typed exactly application/pdf, so the viewer is chosen
 * by its type whatever parameters the response's type carried.
 */

export interface ViewerTarget {
  sourceId: string
  title: string
  /** 1-based; the PDF opens here. */
  page: number | null
}

type Loaded = { key: string; url: string; mime: string; fileName: string } | { key: string; failed: true }

const PDF = 'application/pdf'

/** False only when the browser says it cannot show a PDF inline; unknown counts as "try". */
function canShowPdf(): boolean {
  return typeof navigator === 'undefined' || (navigator as { pdfViewerEnabled?: boolean }).pdfViewerEnabled !== false
}

export function KnowledgeDocumentViewer({
  target,
  realm,
  onClose,
}: {
  target: ViewerTarget | null
  realm: KnowledgeFileRealm
  onClose: () => void
}) {
  const { t } = useTranslation()
  const sourceId = target?.sourceId ?? null
  const key = sourceId ? `${realm}:${sourceId}` : null
  const [loaded, setLoaded] = useState<Loaded | null>(null)
  const returnFocus = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!key || !sourceId) return
    let cancelled = false
    let url: string | null = null
    fetchKnowledgeFile(realm, sourceId).then(
      (file) => {
        if (cancelled) return
        url = URL.createObjectURL(file.mime === PDF ? new Blob([file.blob], { type: PDF }) : file.blob)
        setLoaded({ key, url, mime: file.mime, fileName: file.fileName })
      },
      () => {
        if (!cancelled) setLoaded({ key, failed: true })
      },
    )
    return () => {
      cancelled = true
      if (url) URL.revokeObjectURL(url)
    }
  }, [key, realm, sourceId])

  const current = loaded && loaded.key === key ? loaded : null
  const ready = current && !('failed' in current) ? current : null
  const isPdf = ready?.mime === PDF
  const page = target?.page && target.page > 0 ? target.page : 1
  const title = target?.title ?? ''

  return (
    <Dialog open={target !== null} onOpenChange={(open) => (!open ? onClose() : undefined)}>
      <DialogContent
        showCloseButton={false}
        aria-describedby={undefined}
        className="flex h-[92vh] w-[calc(100vw-1rem)] max-w-5xl flex-col gap-3 p-3 sm:max-w-5xl sm:p-4"
        onOpenAutoFocus={() => {
          returnFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault()
          returnFocus.current?.focus()
        }}
      >
        <div className="flex items-center gap-2">
          <DialogTitle className="min-w-0 flex-1 truncate text-base">
            <UserText>{title}</UserText>
          </DialogTitle>
          {ready ? (
            <Button asChild variant="outline" size="sm">
              <a href={ready.url} download={ready.fileName}>
                <Download aria-hidden="true" />
                {t('knowledgePortal.viewer.download')}
              </a>
            </Button>
          ) : null}
          <Button type="button" variant="ghost" size="icon" aria-label={t('knowledgePortal.viewer.close')} onClick={onClose}>
            <X aria-hidden="true" />
          </Button>
        </div>
        <div className="flex min-h-0 flex-1 flex-col">
          {!current ? (
            <p role="status" className="m-auto flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              {t('knowledgePortal.viewer.loading')}
            </p>
          ) : !ready ? (
            <p role="alert" className="m-auto text-sm text-muted-foreground">
              {t('knowledgePortal.viewer.error')}
            </p>
          ) : isPdf && canShowPdf() ? (
            <object
              title={title}
              type={PDF}
              data={`${ready.url}#page=${page}`}
              className="flex h-full w-full flex-1 rounded-md border bg-muted"
            >
              <PdfFallback url={ready.url} fileName={ready.fileName} />
            </object>
          ) : isPdf ? (
            <div className="flex h-full w-full flex-1 rounded-md border bg-muted">
              <PdfFallback url={ready.url} fileName={ready.fileName} />
            </div>
          ) : (
            <p className="m-auto max-w-sm text-center text-sm text-muted-foreground">{t('knowledgePortal.viewer.notPreviewable')}</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

/** In the frame, where the PDF would be: what to do when it does not show. */
function PdfFallback({ url, fileName }: { url: string; fileName: string }) {
  const { t } = useTranslation()
  return (
    <div className="m-auto flex max-w-sm flex-col items-center gap-3 p-6 text-center">
      <p className="text-sm text-muted-foreground">{t('knowledgePortal.viewer.fallback')}</p>
      <Button asChild size="sm">
        <a href={url} download={fileName}>
          <Download aria-hidden="true" />
          {t('knowledgePortal.viewer.download')}
        </a>
      </Button>
    </div>
  )
}
